import { ReactNode } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { RacingRow, useRacingDashboard, useRoiDashboard } from '../../queries/racingAnalytics';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { StatCard } from '../../components/ui/StatCard';
import { Spinner } from '../../components/ui/Spinner';

const num = (v: RacingRow[string] | undefined) => (v == null ? 0 : Number(v));
const pct = (v: RacingRow[string] | undefined) => `${num(v).toFixed(2)}%`;
const roi = (v: RacingRow[string] | undefined) => {
  const n = num(v);
  const tone = n > 0 ? 'text-emerald-600 dark:text-emerald-400' : n < 0 ? 'text-red-600 dark:text-red-400' : 'text-muted-foreground';
  return <span className={`font-medium ${tone}`}>{n > 0 ? '+' : ''}{n.toFixed(2)}%</span>;
};

// Shared theme-aware axis/grid/tooltip props for the recharts line charts.
const AXIS = { tick: { fontSize: 11, fill: 'hsl(var(--muted-foreground))' }, stroke: 'hsl(var(--border))' };
const TOOLTIP_STYLE = { fontSize: 12, background: 'hsl(var(--popover))', border: '1px solid hsl(var(--border))', borderRadius: 8, color: 'hsl(var(--popover-foreground))' };

function MiniTable({ title, rows, cols }: { title: string; rows: RacingRow[]; cols: { header: string; render: (r: RacingRow) => ReactNode }[] }) {
  return (
    <Card>
      <CardHeader><CardTitle>{title}</CardTitle></CardHeader>
      <CardContent className="pt-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-xs font-medium uppercase tracking-wide text-muted-foreground">
              <tr>{cols.map((c) => <th key={c.header} className="py-1.5 pr-3">{c.header}</th>)}</tr>
            </thead>
            <tbody>
              {rows.map((r, i) => <tr key={i} className="border-t border-border">{cols.map((c) => <td key={c.header} className="py-1.5 pr-3">{c.render(r)}</td>)}</tr>)}
              {rows.length === 0 && <tr><td colSpan={cols.length} className="py-4 text-center text-muted-foreground">No data.</td></tr>}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

export function DashboardTab({ analysisTypeId }: { analysisTypeId: number }) {
  const { data, isLoading } = useRacingDashboard(analysisTypeId);
  if (isLoading || !data) return <Spinner />;
  const k = data.kpis;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
        <StatCard label="Total Races" value={num(k?.total_races).toLocaleString()} />
        <StatCard label="Top-1 Win" value={pct(k?.top_1_win_pct)} />
        <StatCard label="Top-3 Win" value={pct(k?.top_3_win_pct)} />
        <StatCard label="Value Win" value={pct(k?.value_win_pct)} />
        <StatCard label="Exacta Hit" value={pct(k?.exacta_hit_rate)} />
      </div>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <MiniTable title="By track type" rows={data.byTrackType} cols={[
          { header: 'Type', render: (r) => r.track_type },
          { header: 'Top-1', render: (r) => pct(r.top_1_win_pct) },
          { header: 'Top-3', render: (r) => pct(r.top_3_win_pct) },
          { header: 'Value', render: (r) => pct(r.value_win_pct) },
        ]} />
        <MiniTable title="By state" rows={data.byState} cols={[
          { header: 'State', render: (r) => String(r.state ?? '').toUpperCase() },
          { header: 'Races', render: (r) => num(r.total_races).toLocaleString() },
          { header: 'Top-1', render: (r) => pct(r.top_1_win_pct) },
        ]} />
        <MiniTable title="By day of week" rows={data.byDayOfWeek} cols={[
          { header: 'Day', render: (r) => r.day_name },
          { header: 'Races', render: (r) => num(r.total_races).toLocaleString() },
          { header: 'Top-1', render: (r) => pct(r.top_1_win_pct) },
        ]} />
        <MiniTable title="By race class (≥50 races)" rows={data.byRaceClass} cols={[
          { header: 'Class', render: (r) => r.class },
          { header: 'Races', render: (r) => num(r.total_races).toLocaleString() },
          { header: 'Top-1', render: (r) => pct(r.top_1_win_pct) },
        ]} />
      </div>
      <Card>
        <CardHeader><CardTitle>Monthly Top-1 win rate</CardTitle></CardHeader>
        <CardContent className="pt-0">
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data.monthlyTrend.map((m) => ({ month: m.month, top1: num(m.top_1_win_pct) }))}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="month" interval="preserveStartEnd" {...AXIS} />
                <YAxis {...AXIS} />
                <Tooltip contentStyle={TOOLTIP_STYLE} cursor={{ stroke: 'hsl(var(--border))' }} />
                <Line type="monotone" dataKey="top1" name="Top-1 win %" stroke="hsl(var(--primary))" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

const ROI_PICKS = [
  { key: 'top_1_runner', label: 'Top 1' },
  { key: 'top_2_runner', label: 'Top 2' },
  { key: 'top_3_runner', label: 'Top 3' },
  { key: 'value_runner', label: 'Value' },
];

export function RoiDashboardTab({ analysisTypeId }: { analysisTypeId: number }) {
  const { data, isLoading } = useRoiDashboard(analysisTypeId);
  if (isLoading || !data) return <Spinner />;
  const k = data.kpis;
  return (
    <div className="space-y-4">
      <p className="text-xs text-muted-foreground">ROI at the analysis-time price vs the starting price (SP), over the odds-matched race set.</p>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {ROI_PICKS.map((p) => (
          <Card key={p.key} className="p-4">
            <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{p.label}</div>
            <div className="mt-1.5 text-sm">Bets: <span className="font-medium tabular-nums">{num(k?.[`${p.key}_bets`]).toLocaleString()}</span></div>
            <div className="text-sm">Analysis ROI: {roi(k?.[`${p.key}_roi_analysis`])}</div>
            <div className="text-sm">SP ROI: {roi(k?.[`${p.key}_roi_sp`])}</div>
          </Card>
        ))}
      </div>

      <Card>
        <CardHeader><CardTitle>Cumulative Top-1 P&amp;L (units) — analysis vs SP</CardTitle></CardHeader>
        <CardContent className="pt-0">
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data.monthly.map((m) => ({ month: m.month, analysis: num(m.cumulative_pl_analysis), sp: num(m.cumulative_pl_sp) }))}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="month" interval="preserveStartEnd" {...AXIS} />
                <YAxis {...AXIS} />
                <Tooltip contentStyle={TOOLTIP_STYLE} cursor={{ stroke: 'hsl(var(--border))' }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Line type="monotone" dataKey="analysis" name="Analysis price" stroke="#3B82F6" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="sp" name="Starting price" stroke="hsl(var(--primary))" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      <MiniTable title="Top-1 by analysis price bucket" rows={data.byPriceBucket} cols={[
        { header: 'Price', render: (r) => r.price_bucket },
        { header: 'Bets', render: (r) => num(r.bets).toLocaleString() },
        { header: 'Win %', render: (r) => pct(r.win_pct) },
        { header: 'Analysis ROI', render: (r) => roi(r.roi_analysis) },
        { header: 'SP ROI', render: (r) => roi(r.roi_sp) },
      ]} />
    </div>
  );
}
